// useAsientoFormXL - Sprint 44 Fase 3 Vista 8.
// Hook form de asiento con load/save/refresh/publicar + msg auto-dismiss.
// Reusa contabilidadService legacy. Cero backend touch.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as svc from '../services/contabilidadService';

const MSG_AUTO_DISMISS_MS = 4000;

const EMPTY = {
  id: null,
  numero: '',
  fecha: new Date().toISOString().slice(0, 10),
  descripcion: '',
  modulo_origen: 'manual',
  referencia: '',
  total_debito: 0,
  total_credito: 0,
  estado: 'borrador',
  balanceado: false,
  diferencia: 0,
};

export default function useAsientoFormXL() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'nuevo';

  const [form, setForm] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [msg, setMsg] = useState(null);

  const msgTimer = useRef(null);
  const pushMsg = useCallback((kind, text) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    setMsg({ kind, text });
    msgTimer.current = setTimeout(() => setMsg(null), MSG_AUTO_DISMISS_MS);
  }, []);
  useEffect(() => () => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (isNew) {
        if (alive) { setForm(EMPTY); setLoaded(true); }
        return;
      }
      try {
        const d = await svc.getAsiento(id);
        if (alive) setForm({ ...EMPTY, ...(d || {}) });
      } catch (e) {
        if (alive) pushMsg('err', 'No se pudo cargar el asiento');
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [id, isNew, pushMsg]);

  const set = useCallback((k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
  }, []);

  const onSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        fecha: form.fecha,
        descripcion: form.descripcion || '',
        referencia: form.referencia || '',
        modulo_origen: 'manual',
      };
      if (isNew) {
        const r = await svc.createAsiento(payload);
        pushMsg('ok', 'Asiento creado');
        if (r && r.id) navigate('/contabilidad/asientos/' + r.id, { replace: true });
      } else {
        const r = await svc.updateAsiento(id, payload);
        if (r) setForm((f) => ({ ...f, ...r }));
        pushMsg('ok', 'Asiento actualizado');
      }
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }, [isNew, id, form.fecha, form.descripcion, form.referencia, navigate, pushMsg]);

  const refresh = useCallback(async () => {
    if (isNew) return;
    try {
      const d = await svc.getAsiento(id);
      if (d) setForm((f) => ({ ...f, ...d }));
    } catch (_) { /* no bloquea */ }
  }, [isNew, id]);

  const onPublicar = useCallback(async () => {
    if (!window.confirm('Publicar asiento? No se podra editar despues.')) return;
    setPublishing(true);
    try {
      const r = await svc.publicarAsiento(id);
      if (r) setForm((f) => ({ ...f, ...r }));
      pushMsg('ok', 'Asiento publicado');
    } catch (e) {
      const detail = (e && e.data && e.data.message) || (e && e.message) || 'Error al publicar';
      pushMsg('err', detail);
    } finally {
      setPublishing(false);
    }
  }, [id, pushMsg]);

  return {
    id, isNew, navigate,
    form, set,
    loaded, saving, publishing,
    msg, pushMsg, clearMsg: () => setMsg(null),
    onSave, onPublicar, refresh,
  };
}
